import { useEffect, useState } from 'react';
import { Card, Spinner, Alert, Button, Form, Row, Col, Badge } from 'react-bootstrap';
import { apiGet } from '../services/apiClient';

const statusOptions = [
  'pending',
  'awaiting_payment',
  'confirmed',
  'checked_in',
  'checked_out',
  'completed',
  'cancelled',
  'declined',
];

const BookingsCalendarPage = ({ token }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });
  const [status, setStatus] = useState('');

  const load = async (next = {}) => {
    const activeRange = next.range || range;
    const activeStatus =
      Object.prototype.hasOwnProperty.call(next, 'status') ? next.status : status;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (activeRange.from) params.append('from', activeRange.from);
      if (activeRange.to) params.append('to', activeRange.to);
      if (activeStatus) params.append('status', activeStatus);
      const res = await apiGet(`/api/admin/bookings/calendar?${params.toString()}`, token);
      setEvents(res || []);
    } catch (err) {
      setError('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-4 bg-white rounded shadow-sm">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-0">Booking Calendar</h4>
          <small className="text-muted">Date-spanning view for bookings.</small>
        </div>
      </div>
      <Form className="mb-3" onSubmit={(e) => e.preventDefault()}>
        <Row className="g-2">
          <Col md={3}>
            <Form.Control
              type="date"
              value={range.from}
              onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))}
            />
          </Col>
          <Col md={3}>
            <Form.Control
              type="date"
              value={range.to}
              onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))}
            />
          </Col>
          <Col md={3}>
            <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col className="d-flex gap-2">
            <Button onClick={load}>Apply</Button>
            <Button
              variant="outline-secondary"
              onClick={() => {
                const clearedRange = { from: '', to: '' };
                setRange(clearedRange);
                setStatus('');
                load({ range: clearedRange, status: '' });
              }}
            >
              Clear
            </Button>
          </Col>
        </Row>
      </Form>
      {loading && (
        <div className="py-4 text-center">
          <Spinner animation="border" />
        </div>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {!loading && !error && (
        <div className="d-grid gap-3">
          {events.map((ev) => (
            <Card key={ev.id}>
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="fw-semibold">{ev.title}</div>
                  <div className="text-muted small">
                    {ev.start} → {ev.end}
                  </div>
                  <div className="text-muted small">
                    {ev.customerName} • {ev.propertyName}
                  </div>
                </div>
                <div className="text-end">
                  <Badge bg="secondary" className="text-capitalize">
                    {ev.booking_status}
                  </Badge>
                </div>
              </Card.Body>
            </Card>
          ))}
          {events.length === 0 && <div className="text-muted">No events for this range.</div>}
        </div>
      )}
    </div>
  );
};

export default BookingsCalendarPage;
